/**
 * True once the company doc has loaded, so its plan is actually known.
 *
 * `company?.subscriptionPlanId === "pro_plan_id"` alone reads a missing company as a
 * non-Pro one, which flashes the free (locked) layout at Pro clients while the company
 * is still loading or when its fetch failed. Gate plan-dependent UI on this first.
 */
export function isPlanResolved(company: any): boolean {
  return !!company && typeof company === "object" && Object.keys(company).length > 0;
}

/**
 * Tech Performance plan (leaderboard and Insights Hub only, everything else locked).
 *
 * A Tech Performance company stores the plan's real Stripe price id in `subscriptionPlanId` (and in
 * `productAccess.plan`): the price from STRIPE_TECH_PERFORMANCE_PRICE_ID. A company is on this plan
 * when its plan id equals that env price id. Browser code cannot read server env, so the same value
 * is also read from NEXT_PUBLIC_STRIPE_TECH_PERFORMANCE_PRICE_ID (a price id is not a secret).
 *
 * TECH_PERFORMANCE_PLAN_ID is the old stored value, still recognised for companies created before
 * the price id was stored. It is also the stable plan key the admin APIs report for this plan (see
 * adminPlanKey), so the admin panel needs no Stripe env of its own.
 */
export const TECH_PERFORMANCE_PLAN_ID = "tech_performance_plan_id";

/**
 * Every Tech Performance price id in env. The value may be a comma-separated list: the FIRST id is the
 * current price (new customers and the payment link), the rest are older prices that existing
 * customers are still stored with. When the price changes, put the new id first and keep the old ones,
 * or those customers stop being recognised as this plan.
 */
export function techPerformancePriceIds(): string[] {
  // Literal process.env reads so Next inlines the public one into browser bundles.
  const raw =
    process.env.NEXT_PUBLIC_STRIPE_TECH_PERFORMANCE_PRICE_ID ||
    process.env.STRIPE_TECH_PERFORMANCE_PRICE_ID ||
    "";
  return raw
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

/** The current Tech Performance Stripe price id (the first in env), or null when it is not configured. */
export function techPerformancePriceId(): string | null {
  return techPerformancePriceIds()[0] ?? null;
}

/** Every plan id that means Tech Performance: the env price ids and the legacy key. For Mongo `$in` / `$nin`. */
export function techPerformancePlanIds(): string[] {
  return [...techPerformancePriceIds(), TECH_PERFORMANCE_PLAN_ID];
}

/**
 * True for a company on the Tech Performance plan. Accepts a company doc or a bare plan id.
 *
 * Check this before any `=== "pro_plan_id"` gate: every such gate reads this plan as "not pro" and
 * would otherwise hand it the basic plan's behaviour (recovery, reports, partial locks). The Insights
 * Hub hides TECH_PERFORMANCE_LOCKED_INSIGHT_COLUMN_NAMES (utils/tableInsightUtils.ts) for this plan.
 */
export function isTechPerformancePlan(companyOrPlanId: any): boolean {
  // The server works this out with its env (STRIPE_TECH_PERFORMANCE_PRICE_ID) and sends it on every
  // company it returns to the browser (/api/company, /api/user/me, login). Trust that flag first, so
  // browser code does not depend on the public env var being set in every environment.
  if (companyOrPlanId && typeof companyOrPlanId === "object" && typeof companyOrPlanId.isTechPerformancePlan === "boolean") {
    return companyOrPlanId.isTechPerformancePlan;
  }
  const planId =
    typeof companyOrPlanId === "string" ? companyOrPlanId : companyOrPlanId?.subscriptionPlanId;
  return !!planId && techPerformancePlanIds().includes(planId);
}

/**
 * The plan id as the admin panel knows it: TECH_PERFORMANCE_PLAN_ID for any Tech Performance
 * company (whatever price id is stored), every other plan id unchanged. Admin APIs return this, and
 * the plan-change route accepts it back and stores the real price id.
 */
export function adminPlanKey(companyOrPlanId: any): string | null {
  const planId =
    typeof companyOrPlanId === "string" ? companyOrPlanId : companyOrPlanId?.subscriptionPlanId;
  if (!planId) return null;
  return isTechPerformancePlan(planId) ? TECH_PERFORMANCE_PLAN_ID : planId;
}

/** What the Company's productAccess holds for a Tech Performance company. */
export function techPerformanceProductAccess(planId: string) {
  return {
    plan: planId,
    techPerformance: { enabled: true, displayEnabled: true },
  };
}
